import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  fetchCollection,
  fetchCollectionHistory,
  importCollectionReplace,
  putCollectionRelease,
  undoCollectionActivity,
} from "../api/collection-api";
import type { ApiClient } from "../api/client";
import { apiClient } from "../api/client";
import { ApiError, NetworkError, userFacingMessage } from "../api/errors";
import type { CollectionItem, PublicRelease } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import {
  clearAllCollectionSnapshots,
  loadCollectionSnapshot,
  saveCollectionSnapshot,
} from "../offline/collection-snapshot-store";
import { useNetworkStatus } from "../offline/NetworkStatusContext";
import {
  CollectionContext,
  type CollectionStatus,
} from "./CollectionContext";

type CollectionProviderProps = {
  children: ReactNode;
  client?: ApiClient;
};

function toMap(items: CollectionItem[]): Map<string, CollectionItem> {
  return new Map(items.map((item) => [item.releaseId, item]));
}

/**
 * Single client owner for authoritative collection snapshot + mutations.
 * Backend revision remains the only write authority.
 * Offline last-known snapshot is account-scoped and read-only.
 */
export function CollectionProvider({
  children,
  client = apiClient,
}: CollectionProviderProps) {
  const auth = useAuth();
  const { online } = useNetworkStatus();
  const authStatus = auth.status;
  const accountId =
    auth.status === "authenticated" ? auth.account.accountId : null;
  const [status, setStatus] = useState<CollectionStatus>("idle");
  const [revision, setRevision] = useState<number | null>(null);
  const [itemsByReleaseId, setItemsByReleaseId] = useState<
    Map<string, CollectionItem>
  >(() => new Map());
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [conflictMessage, setConflictMessage] = useState<string | null>(null);
  const [mutatingReleaseId, setMutatingReleaseId] = useState<string | null>(
    null,
  );
  const [stale, setStale] = useState(false);
  const [undoToast, setUndoToast] = useState<{ activityId: string; message: string } | null>(null);
  const revisionRef = useRef<number | null>(null);
  const mutatingRef = useRef<string | null>(null);
  const accountIdRef = useRef<string | null>(null);

  useEffect(() => {
    revisionRef.current = revision;
  }, [revision]);

  useEffect(() => {
    accountIdRef.current = accountId;
  }, [accountId]);

  const persistSnapshot = useCallback(
    (accountId: string, snapshot: { revision: number; items: CollectionItem[] }) => {
      saveCollectionSnapshot(accountId, snapshot);
    },
    [],
  );

  const applySnapshot = useCallback(
    (
      snapshot: { revision: number; items: CollectionItem[] },
      options?: { stale?: boolean; accountId?: string },
    ) => {
      setRevision(snapshot.revision);
      setItemsByReleaseId(toMap(snapshot.items));
      setStatus("ready");
      setErrorMessage(null);
      setStale(options?.stale === true);
      const accountId = options?.accountId ?? accountIdRef.current;
      if (accountId && options?.stale !== true) {
        persistSnapshot(accountId, snapshot);
      }
    },
    [persistSnapshot],
  );

  const refresh = useCallback(async () => {
    setStatus("loading");
    setErrorMessage(null);
    try {
      const snapshot = await fetchCollection(client);
      applySnapshot(snapshot, { stale: false });
    } catch (error) {
      if (error instanceof ApiError && error.code === "AUTH_REQUIRED") {
        setStatus("idle");
        setRevision(null);
        setItemsByReleaseId(new Map());
        setStale(false);
        return;
      }
      if (error instanceof NetworkError) {
        const accountId = accountIdRef.current;
        if (accountId) {
          const cached = loadCollectionSnapshot(accountId);
          if (cached) {
            applySnapshot(cached.snapshot, { stale: true, accountId });
            return;
          }
        }
      }
      setStatus("error");
      setErrorMessage(userFacingMessage(error));
      setStale(false);
    }
  }, [applySnapshot, client]);

  useEffect(() => {
    if (authStatus !== "authenticated" || accountId === null) {
      if (authStatus === "unauthenticated") {
        clearAllCollectionSnapshots();
      }
      setStatus("idle");
      setRevision(null);
      setItemsByReleaseId(new Map());
      setErrorMessage(null);
      setConflictMessage(null);
      setMutatingReleaseId(null);
      setStale(false);
      mutatingRef.current = null;
      return;
    }

    let cancelled = false;
    setStatus("loading");
    setErrorMessage(null);

    void (async () => {
      try {
        const snapshot = await fetchCollection(client);
        if (!cancelled) {
          applySnapshot(snapshot, { stale: false, accountId });
        }
      } catch (error) {
        if (cancelled) {
          return;
        }
        if (error instanceof ApiError && error.code === "AUTH_REQUIRED") {
          setStatus("idle");
          return;
        }
        if (error instanceof NetworkError) {
          const cached = loadCollectionSnapshot(accountId);
          if (cached) {
            applySnapshot(cached.snapshot, { stale: true, accountId });
            return;
          }
        }
        setStatus("error");
        setErrorMessage(userFacingMessage(error));
        setStale(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authStatus, accountId, applySnapshot, client]);

  const wasOfflineRef = useRef(false);

  // Online recovery: refresh authoritative snapshot; never replay mutations.
  useEffect(() => {
    if (!online) {
      wasOfflineRef.current = true;
      return;
    }
    if (!wasOfflineRef.current) {
      return;
    }
    wasOfflineRef.current = false;
    if (authStatus !== "authenticated") {
      return;
    }
    void refresh();
  }, [online, authStatus, refresh]);

  const getQuantity = useCallback(
    (releaseId: string) => itemsByReleaseId.get(releaseId)?.quantity ?? 0,
    [itemsByReleaseId],
  );

  const setQuantity = useCallback(
    async (releaseId: string, quantity: number) => {
      if (!online || stale) {
        setErrorMessage("Изменения недоступны без сети.");
        return;
      }
      if (quantity < 0) {
        return;
      }
      if (mutatingRef.current !== null) {
        return;
      }
      const expectedRevision = revisionRef.current;
      if (expectedRevision === null) {
        setErrorMessage("Коллекция ещё не загружена.");
        return;
      }

      mutatingRef.current = releaseId;
      setMutatingReleaseId(releaseId);
      setConflictMessage(null);
      setErrorMessage(null);

      try {
        const previous = itemsByReleaseId.get(releaseId);
        const result = await putCollectionRelease(client, releaseId, {
          quantity,
          expectedRevision,
        });

        setRevision(result.revision);
        setItemsByReleaseId((prev) => {
          const next = new Map(prev);
          if (result.item === null) {
            next.delete(releaseId);
          } else {
            next.set(releaseId, result.item);
          }
          const accountId = accountIdRef.current;
          if (accountId) {
            persistSnapshot(accountId, {
              revision: result.revision,
              items: Array.from(next.values()),
            });
          }
          return next;
        });
        setStatus("ready");
        setStale(false);
        const history = await fetchCollectionHistory(client);
        const activity = history.items.find((item) => item.release.releaseId === releaseId && item.delta === quantity - (previous?.quantity ?? 0) && (item.kind === "ADD" || item.kind === "REMOVE"));
        if (activity) {
          const release = result.item?.release ?? previous?.release;
          setUndoToast({ activityId: activity.activityId, message: `${release?.casting.name ?? "Модель"} ${quantity > (previous?.quantity ?? 0) ? "добавлена" : "удалена"}` });
        }
      } catch (error) {
        if (error instanceof ApiError && error.code === "COLLECTION_CONFLICT") {
          try {
            const snapshot = await fetchCollection(client);
            applySnapshot(snapshot, { stale: false });
            setConflictMessage(userFacingMessage(error));
          } catch (refreshError) {
            setErrorMessage(userFacingMessage(refreshError));
          }
          return;
        }
        if (error instanceof ApiError && error.code === "AUTH_REQUIRED") {
          return;
        }
        setErrorMessage(userFacingMessage(error));
      } finally {
        mutatingRef.current = null;
        setMutatingReleaseId(null);
      }
    },
    [applySnapshot, client, itemsByReleaseId, online, persistSnapshot, stale],
  );

  const undoActivity = useCallback(async (activityId: string) => {
    const expectedRevision = revisionRef.current;
    if (expectedRevision === null || !online || stale || mutatingRef.current !== null) return;
    mutatingRef.current = "__undo__";
    setMutatingReleaseId("__undo__");
    try {
      await undoCollectionActivity(client, activityId, expectedRevision);
      setUndoToast(null);
      await refresh();
    } catch (error) {
      setErrorMessage(userFacingMessage(error));
      await refresh();
    } finally { mutatingRef.current = null; setMutatingReleaseId(null); }
  }, [client, online, refresh, stale]);

  const dismissUndoToast = useCallback(() => setUndoToast(null), []);

  const importReplace = useCallback(
    async (items: Array<{ releaseId: string; quantity: number }>) => {
      if (!online || stale) {
        throw new Error("Импорт недоступен без сети.");
      }
      if (mutatingRef.current !== null) {
        throw new Error("Дождитесь завершения текущего изменения.");
      }
      const expectedRevision = revisionRef.current;
      if (expectedRevision === null) {
        throw new Error("Коллекция ещё не загружена.");
      }

      mutatingRef.current = "__import__";
      setMutatingReleaseId("__import__");
      setConflictMessage(null);
      setErrorMessage(null);

      try {
        const snapshot = await importCollectionReplace(client, {
          expectedRevision,
          mode: "replace",
          items,
        });
        applySnapshot(snapshot, { stale: false });
        return snapshot;
      } catch (error) {
        if (error instanceof ApiError && error.code === "COLLECTION_CONFLICT") {
          try {
            const snapshot = await fetchCollection(client);
            applySnapshot(snapshot, { stale: false });
            setConflictMessage(userFacingMessage(error));
          } catch (refreshError) {
            setErrorMessage(userFacingMessage(refreshError));
          }
        } else if (!(error instanceof ApiError && error.code === "AUTH_REQUIRED")) {
          setErrorMessage(userFacingMessage(error));
        }
        throw error;
      } finally {
        mutatingRef.current = null;
        setMutatingReleaseId(null);
      }
    },
    [applySnapshot, client, online, stale],
  );

  const exportFresh = useCallback(async () => {
    if (!online) {
      throw new Error("Экспорт доступен только при подключении к сети.");
    }
    const snapshot = await fetchCollection(client);
    applySnapshot(snapshot, { stale: false });
    return snapshot;
  }, [applySnapshot, client, online]);

  const items = useMemo(
    () => Array.from(itemsByReleaseId.values()),
    [itemsByReleaseId],
  );
  const uniqueCount = items.length;
  const totalQuantity = useMemo(
    () => items.reduce((sum, item) => sum + item.quantity, 0),
    [items],
  );

  const mutationsDisabled = !online || stale || revision === null;

  const patchRelease = useCallback((release: PublicRelease) => {
    setItemsByReleaseId((prev) => {
      const existing = prev.get(release.releaseId);
      if (!existing) {
        return prev;
      }
      const next = new Map(prev);
      next.set(release.releaseId, {
        ...existing,
        release: {
          ...existing.release,
          primaryImage: release.primaryImage,
        },
      });
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({
      status,
      revision,
      itemsByReleaseId,
      items,
      uniqueCount,
      totalQuantity,
      errorMessage,
      conflictMessage,
      mutatingReleaseId,
      stale,
      online,
      mutationsDisabled,
      clearConflictMessage: () => setConflictMessage(null),
      refresh,
      getQuantity,
      setQuantity,
      undoActivity,
      undoToast,
      dismissUndoToast,
      importReplace,
      exportFresh,
      patchRelease,
    }),
    [
      status,
      revision,
      itemsByReleaseId,
      items,
      uniqueCount,
      totalQuantity,
      errorMessage,
      conflictMessage,
      mutatingReleaseId,
      stale,
      online,
      mutationsDisabled,
      refresh,
      getQuantity,
      setQuantity,
      undoActivity,
      undoToast,
      dismissUndoToast,
      importReplace,
      exportFresh,
      patchRelease,
    ],
  );

  return (
    <CollectionContext.Provider value={value}>
      {children}
    </CollectionContext.Provider>
  );
}
